import FilterIcon from '@mui/icons-material/TuneOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns';

import Button from '@/components/Button';
import GenericDrawer from '@/components/GenericDrawer';
import { AgaveDataGrid } from '@/components/GenericTable/DataGridStyled';
import DownloadModal from '@/components/Modals/DownloadModal/DownloadModal';
import SearchInput from '@/components/SearchInput';

import { getGenerateGuidesModal } from './constants';
import Filters from './dependencies/Filters/Filters';
import CreateOrderModal from './dependencies/modals/CreateOrderModal';
import DetailsModalPlugins from './dependencies/modals/DetailsModal/DetailsModalPlugins';
import ValidateCoverage from './dependencies/modals/ValidateCoverage';
import { envioflexColumns } from './envioflex-columns';
import useEnvioFlex from './useEnvioFlex';

const EnvioFlex = () => {
  const hook = useEnvioFlex();
  const theme = useTheme();

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      alignItems="end"
      justifyContent="center"
      gap="1rem"
    >
      <Button onClick={hook.processPendingOrders}>
        Generar guías de las ventas del día ({format(new Date(), 'dd/MM/yyyy')})
      </Button>
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '1rem',
          padding: `${theme.spacing(4)} 0 0`,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          width="100%"
          height="auto"
          sx={{
            padding: `0 ${theme.spacing(4)}`,
            flexDirection: { xxs: 'column', xl: 'row' },
            gap: { xxs: '1rem', xl: 0 },
            alignItems: 'center',
          }}
        >
          <SearchInput
            buttonText={'Buscar'}
            placeholder={'Buscar por Orden, Paquete (Pack ID) o Envío de MercadoLibre'}
            onChange={(e) => hook.handleSearchChange(e.target.value)}
            value={hook.searchValue}
            onClick={hook.handleSearch}
            handleKeyPress={(e) => {
              if (e.key === 'Enter') hook.handleSearch();
            }}
            sx={{ width: '100%' }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xxs: 'column', xs: 'row' },
              gap: '1rem',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button variant="outlined" onClick={hook.clearFilters} size="small">
              Borrar filtros
            </Button>
            <Box
              display="flex"
              gap="0.5rem"
              alignItems="center"
              justifyContent="center"
              onClick={() => hook.setOpenFilters(true)}
              sx={{
                cursor: 'pointer',
                ':hover': { backgroundColor: theme.palette.primary.lighter },
                padding: '0.5rem',
                borderRadius: '1rem',
              }}
            >
              <FilterIcon color="primary" />

              <Typography variant="body1" fontWeight={theme.typography.fontWeightMedium}>
                Filtros
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box width="100%" mt={2}>
          <Box sx={{ overflowX: 'auto', width: '100%' }}>
            <Box sx={{ minWidth: '700px' }}>
              <AgaveDataGrid
                columns={envioflexColumns({
                  handleCoverageCheck: hook.handleCoverageCheck,
                  handleDownloadLabel: hook.handleDownloadLabel,
                })}
                rows={hook.orders.data || []}
                localeText={{
                  noRowsLabel: 'No hay órdenes disponibles',
                }}
                onCellClick={(params) => {
                  if (!['actions', '__check__'].includes(params.colDef.field)) {
                    hook.handleRowClick(params.row.orderId);
                  }
                }}
                rowCount={hook.orders.total}
                loading={hook.orders.loading}
                disableRowSelectionOnClick={true}
                getRowId={(row) => row.orderId}
                hideFooterSelectedRowCount={true}
                paginationModel={{
                  page: hook.pagination.filters.page as number,
                  pageSize: hook.pagination.filters.page_size as number,
                }}
                disableColumnMenu={true}
                paginationMode="server"
                onPaginationModelChange={hook.pagination.onPaginationModelChange}
              />
            </Box>
          </Box>
        </Box>
      </Paper>
      <GenericDrawer
        open={hook.openFilters}
        onClose={hook.onClose}
        title={'Filtros'}
        disableEscapeKeyDown={true}
        iconTitle={<FilterIcon color="primary" />}
      >
        <Filters
          selectedFilters={hook.filters}
          onChange={hook.onChangeFilter}
          clearFilters={hook.clearFilters}
          applyFilters={hook.applyFilters}
        />
      </GenericDrawer>
      <ValidateCoverage
        isOpen={hook.modals.coverage}
        onClose={() => hook.handleModal('coverage')}
        rateData={hook.rateData}
        loading={hook.loadingRate}
        onCreateOrder={hook.createOrderFromRate}
        orderStatus={hook.selectedOrderStatus}
      />
      <CreateOrderModal
        isOpen={hook.modals.confirmOrder}
        createOrder={hook.createOrderFromRate}
        onClose={() => hook.handleModal('confirmOrder')}
        cost={hook.rateData?.prices.total ?? 0}
      />
      <DownloadModal
        isOpen={hook.modals.downloadLabel}
        onClose={() => hook.handleModal('downloadLabel')}
        trackingIds={hook.selectedTrackingIds}
        downloadLabels={hook.downloadLabels}
        amount
      />
      {getGenerateGuidesModal(
        hook.modals.confirmOrders,
        () => hook.handleModal('confirmOrders'),
        hook.failedOrders,
        hook.successTrackingIds,
      )}
      <DetailsModalPlugins
        isOpen={hook.modals.details}
        onClose={() => hook.handleModal('details')}
        orderId={hook.selectedOrderId}
        clientId={hook.clientId || ''}
      />
    </Box>
  );
};

export default EnvioFlex;
